import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle, User, Buildings, Phone, MapPin, IdentificationCard } from '@phosphor-icons/react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(!token);
  const [loadingInfo, setLoadingInfo] = useState(!!token);
  const [info, setInfo] = useState(null);
  const [profile, setProfile] = useState({ name: '', phone: '', company_name: '', address: '', patient_reference_prefix: '' });

  useEffect(() => {
    if (!token) return;
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/auth/reset-info`, { params: { token } });
        setInfo(res.data);
        setProfile({
          name: res.data.name || '',
          phone: res.data.phone || '',
          company_name: res.data.company_name || '',
          address: res.data.address || '',
          patient_reference_prefix: res.data.patient_reference_prefix || '',
        });
      } catch {
        setInvalid(true);
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInfo();
  }, [token]);

  const setField = (field) => (e) => setProfile((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { toast.error('Veuillez renseigner votre nom'); return; }
    if (password.length < 6) { toast.error('Le mot de passe doit contenir au moins 6 caractères'); return; }
    if (password !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password, profile });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lien invalide ou expiré');
    } finally {
      setIsLoading(false);
    }
  };

  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="bg-white border border-zinc-200 p-8 max-w-md text-center" data-testid="reset-invalid">
          <h2 className="text-xl font-bold mb-2">Lien invalide</h2>
          <p className="text-sm text-zinc-500 mb-4">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Button onClick={() => navigate('/forgot-password')} className="rounded-sm bg-zinc-900 hover:bg-zinc-800">Refaire une demande</Button>
        </div>
      </div>
    );
  }

  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <span className="spinner" data-testid="reset-loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="bg-white border border-zinc-200 p-8" data-testid="reset-password-container">
          {!done ? (
            <>
              <h2 className="text-xl font-bold mb-1">Réinitialisation de votre compte</h2>
              <p className="text-sm text-zinc-500 mb-6">Vérifiez vos informations et choisissez un nouveau mot de passe.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Identifiant (lecture seule) */}
                <div className="space-y-2">
                  <Label className="label-overline">Email (identifiant)</Label>
                  <Input value={info?.email || ''} disabled className="rounded-sm bg-zinc-100 text-zinc-500" data-testid="reset-email-readonly" />
                </div>

                {/* Informations du compte */}
                <div className="border border-zinc-200 rounded-sm p-4 space-y-4 bg-zinc-50/50">
                  <p className="label-overline text-xs text-zinc-600">Vos informations — vérifiez et corrigez si besoin</p>
                  <div className="space-y-2">
                    <Label className="label-overline">Nom complet *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input value={profile.name} onChange={setField('name')} className="pl-10 rounded-sm" required data-testid="reset-name-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input value={profile.phone} onChange={setField('phone')} placeholder="06 12 34 56 78" className="pl-10 rounded-sm" data-testid="reset-phone-input" />
                    </div>
                  </div>
                  {info?.role === 'client' && (
                    <>
                      <div className="space-y-2">
                        <Label className="label-overline">Société / Enseigne</Label>
                        <div className="relative">
                          <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                          <Input value={profile.company_name} onChange={setField('company_name')} className="pl-10 rounded-sm" data-testid="reset-company-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline">Adresse</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                          <Input value={profile.address} onChange={setField('address')} className="pl-10 rounded-sm" data-testid="reset-address-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline">Préfixe référence patient</Label>
                        <div className="relative">
                          <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                          <Input value={profile.patient_reference_prefix} onChange={setField('patient_reference_prefix')} placeholder="Ex: HD-" className="pl-10 rounded-sm" data-testid="reset-prefix-input" />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Nouveau mot de passe */}
                <div className="space-y-2">
                  <Label className="label-overline">Nouveau mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 rounded-sm" required data-testid="reset-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Confirmer le mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input type="password" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10 rounded-sm" required data-testid="reset-confirm-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800" disabled={isLoading} data-testid="reset-submit-btn">
                  {isLoading ? <span className="spinner" /> : 'Valider mes informations et mon mot de passe'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle size={48} className="mx-auto text-emerald-600 mb-4" />
              <h2 className="text-xl font-bold mb-2">Compte mis à jour</h2>
              <p className="text-sm text-zinc-500 mb-6">Vos informations et votre mot de passe ont été enregistrés. Vous pouvez maintenant vous connecter.</p>
              <Button onClick={() => navigate('/login')} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="reset-goto-login">Se connecter</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
